import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  HowToReg,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { examinationAPI, branchAPI, courseAPI, batchAPI } from '../../../services/api';

const PendingApprovals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Filters
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedExamination, setSelectedExamination] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Data
  const [pendingStudents, setPendingStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [examinations, setExaminations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  // Dialog state
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchBranches();
    fetchExaminations();
    fetchCourses();
    fetchBatches();
  }, []);

  useEffect(() => {
    fetchPendingStudents();
  }, [selectedBranch, selectedExamination, selectedCourse, selectedBatch, search, startDate, endDate, page]);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || response.data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchExaminations = async () => {
    try {
      const response = await examinationAPI.getAll();
      setExaminations(response.data.data || response.data.examinations || []);
    } catch (error) {
      console.error('Error fetching examinations:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data.data?.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchPendingStudents = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };

      if (selectedBranch) params.branch = selectedBranch;
      if (selectedExamination) params.examination = selectedExamination;
      if (selectedCourse) params.course = selectedCourse;
      if (selectedBatch) params.batch = selectedBatch;
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await examinationAPI.getPendingStudentExams(params);

      setPendingStudents(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalCount(response.data.pagination?.total || 0);
      setSelectedStudents([]);
    } catch (error) {
      console.error('Error fetching pending students:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedStudents(pendingStudents.map(s => s._id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleBulkApprove = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    try {
      setProcessing(true);
      await examinationAPI.bulkApproveStudentExams(selectedStudents);
      toast.success(`${selectedStudents.length} students approved successfully. Hall tickets sent via email.`);
      fetchPendingStudents();
    } catch (error) {
      console.error('Error approving students:', error);
      toast.error(error.response?.data?.message || 'Failed to approve students');
    } finally {
      setProcessing(false);
    }
  };

  const handleIndividualApprove = async (studentExamId) => {
    try {
      setProcessing(true);
      await examinationAPI.approveStudentExam(studentExamId);
      toast.success('Student approved successfully. Hall ticket sent via email.');
      fetchPendingStudents();
    } catch (error) {
      console.error('Error approving student:', error);
      toast.error(error.response?.data?.message || 'Failed to approve student');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      await examinationAPI.rejectStudentExam(selectedStudent._id, rejectionReason);
      toast.success('Student exam application rejected');
      setRejectDialog(false);
      setRejectionReason('');
      setSelectedStudent(null);
      fetchPendingStudents();
    } catch (error) {
      console.error('Error rejecting student:', error);
      toast.error(error.response?.data?.message || 'Failed to reject student');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (student) => {
    setSelectedStudent(student);
    setRejectDialog(true);
    setRejectionReason('');
  };

  const clearFilters = () => {
    setSelectedBranch('');
    setSelectedExamination('');
    setSelectedCourse('');
    setSelectedBatch('');
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/examinations')}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Pending Approvals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and approve student examination applications
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchPendingStudents}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Branch</InputLabel>
              <Select
                value={selectedBranch}
                onChange={(e) => {
                  setSelectedBranch(e.target.value);
                  setPage(1);
                }}
                label="Branch"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Branches</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch._id} value={branch._id}>
                    {branch.name} ({branch.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Examination</InputLabel>
              <Select
                value={selectedExamination}
                onChange={(e) => {
                  setSelectedExamination(e.target.value);
                  setPage(1);
                }}
                label="Examination"
              >
                <MenuItem value="">All Examinations</MenuItem>
                {examinations.map((exam) => (
                  <MenuItem key={exam._id} value={exam._id}>
                    {exam.name} ({exam.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Course</InputLabel>
              <Select
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setPage(1);
                }}
                label="Course"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courses.map((course) => (
                  <MenuItem key={course._id} value={course._id}>
                    {course.name} ({course.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Batch</InputLabel>
              <Select
                value={selectedBatch}
                onChange={(e) => {
                  setSelectedBatch(e.target.value);
                  setPage(1);
                }}
                label="Batch"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Name or Form Number"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Pending Students Table */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Pending Applications ({totalCount})
          </Typography>
          {selectedStudents.length > 0 && (
            <Stack direction="row" spacing={2}>
              <Chip
                label={`${selectedStudents.length} Selected`}
                color="primary"
                variant="outlined"
              />
              <Button
                variant="contained"
                color="success"
                startIcon={<HowToReg />}
                onClick={handleBulkApprove}
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Bulk Approve'}
              </Button>
            </Stack>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : pendingStudents.length > 0 ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStudents.length === pendingStudents.length}
                        indeterminate={selectedStudents.length > 0 && selectedStudents.length < pendingStudents.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell><strong>Register No.</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Branch</strong></TableCell>
                    <TableCell><strong>Course</strong></TableCell>
                    <TableCell><strong>Examination</strong></TableCell>
                    <TableCell><strong>Submitted By</strong></TableCell>
                    <TableCell><strong>Submitted At</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingStudents.map((studentExam) => (
                    <TableRow key={studentExam._id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedStudents.includes(studentExam._id)}
                          onChange={() => handleSelectStudent(studentExam._id)}
                        />
                      </TableCell>
                      <TableCell>{studentExam.student?.formNumber || 'N/A'}</TableCell>
                      <TableCell>{studentExam.student?.personalInfo?.nameAsPerTC || 'N/A'}</TableCell>
                      <TableCell>{studentExam.branch?.name || 'N/A'}</TableCell>
                      <TableCell>{studentExam.student?.course?.name || 'N/A'}</TableCell>
                      <TableCell>{studentExam.examination?.name || 'N/A'}</TableCell>
                      <TableCell>{studentExam.submittedBy?.name || 'N/A'}</TableCell>
                      <TableCell>
                        {studentExam.submittedAt
                          ? new Date(studentExam.submittedAt).toLocaleDateString('en-IN')
                          : 'N/A'}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircle />}
                            onClick={() => handleIndividualApprove(studentExam._id)}
                            disabled={processing}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={() => openRejectDialog(studentExam)}
                            disabled={processing}
                          >
                            Reject
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HowToReg sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              No pending approvals found.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Student Application</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will reject the student's examination application. They can resubmit after addressing the issue.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a clear reason for rejection..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={processing || !rejectionReason.trim()}
          >
            {processing ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovals;
